<template>
  <div class="enfa-order-history-modal" :class="{ 'is-open': isOpen }">
    <div class="enfa-order-history-backdrop" role="button" tabindex="-1" aria-hidden="true" @click="close"></div>
    <div class="enfa-order-history-panel">
      <div class="enfa-order-history-header">
        <h2>Historial de Pedidos</h2>
        <button class="enfa-order-history-close" aria-label="Cerrar historial" @click="close">×</button>
      </div>
      <div class="enfa-order-history-body">
        <div v-if="isLoading" class="loading-state" role="status" aria-live="polite">
          <p>Cargando historial...</p>
        </div>
        <div v-else-if="error" class="error-state" role="alert" aria-live="assertive">
          <p>{{ error }}</p>
          <button class="btn-retry" type="button" @click="fetchOrders(1)">Reintentar</button>
        </div>
        <div v-else-if="orders.length === 0" class="empty-state" role="status" aria-live="polite">
          <p>No tienes pedidos anteriores.</p>
        </div>
        <div v-else class="orders-list">
          <div v-for="order in orders" :key="order.id" class="order-item">
            <div class="order-item-header">
              <span class="order-id">Pedido #{{ getOrderDisplayId(order) }}</span>
              <span class="order-date">{{ formatDate(order.created_at) }}</span>
            </div>
            <div class="order-item-body">
              <div v-for="item in order.items" :key="item.id" class="product-item">
                <div class="product-info">
                  <span class="product-quantity">{{ item.quantity }}x</span>
                  <span class="product-name">{{ item.name }}</span>
                  <div v-if="item.modifiers && item.modifiers.length > 0" class="item-modifiers">
                    <span v-for="mod in item.modifiers" :key="mod.name" class="modifier-tag">
                      + {{ mod.name }}
                    </span>
                  </div>
                </div>
                <span class="product-price">{{ formatItemPrice(item) }}</span>
              </div>
            </div>
            <div class="order-item-footer">
              <span class="order-total">Total: {{ formatPrice(order.total_amount) }}</span>
              <span class="order-status" :class="`status-${order.workflow_status}`" role="status">{{ order.workflow_status }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { getOrderHistory } from '../core/http';

const isOpen = ref(false);
const isLoading = ref(false);
const error = ref<string | null>(null);
const orders = ref<any[]>([]);

const open = () => {
  isOpen.value = true;
  fetchOrders();
};

const close = () => {
  isOpen.value = false;
};

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') close();
};

watch(isOpen, (open) => {
  if (open) {
    window.addEventListener('keydown', handleKeydown);
  } else {
    window.removeEventListener('keydown', handleKeydown);
  }
});

const fetchOrders = async (retries = 1) => {
  isLoading.value = true;
  error.value = null;
  try {
    const response = await getOrderHistory();
    const rawOrders =
      (response as any)?.orders ??
      (response as any)?.data?.orders ??
      (Array.isArray(response) ? response : []);
    orders.value = Array.isArray(rawOrders) ? rawOrders : [];
  } catch (err) {
    if (retries > 0) {
      setTimeout(() => fetchOrders(retries - 1), 150);
      return;
    }
    error.value = 'No se pudo cargar el historial de pedidos.';
  } finally {
    if (retries === 0 || !error.value) {
      isLoading.value = false;
    }
  }
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('es-ES', {
    year: 'numeric', month: 'long', day: 'numeric'
  });
};

const getOrderDisplayId = (order: any): string => {
  const preferred =
    order?.order_number ?? order?.order_id ?? order?.display_order_id ?? order?.public_order_id;
  const preferredText = String(preferred ?? '').trim();
  if (preferredText) return preferredText;

  const normalized = String(order?.id ?? '').trim();
  const compact = normalized.replace(/[^a-zA-Z0-9]/g, '');
  if (compact.length <= 10) return compact || normalized;
  return `${compact.slice(0, 5)}...${compact.slice(-5)}`;
};

const formatPrice = (value: number): string => {
  return `$${(value || 0).toFixed(2)}`;
};

// Line total with the backend semantics (serialize_order_item / item_processor):
// unit_price is the base unit price; each modifier carries its per-unit
// adjustment as `price` and its TOTAL quantity (modifier qty x item qty).
const formatItemPrice = (item: any): string => {
  const quantity = Number(item?.quantity || 1);
  const basePrice = Number(item?.unit_price ?? item?.price ?? item?.subtotal ?? 0);
  const modifiersPrice = Array.isArray(item?.modifiers)
    ? item.modifiers.reduce(
        (sum: number, mod: any) =>
          sum + Number(mod?.price ?? mod?.unit_price_adjustment ?? 0) * Number(mod?.quantity ?? quantity),
        0
      )
    : 0;
  const totalPrice = basePrice * quantity + modifiersPrice;
  return formatPrice(totalPrice > 0 ? totalPrice : basePrice);
};

// Expose open/close to be called from outside
defineExpose({ open, close });
</script>

<style scoped>
.enfa-order-history-modal {
  display: none;
  position: fixed;
  inset: 0;
  z-index: 13100;
  isolation: isolate;
}
.enfa-order-history-modal.is-open {
  display: block !important;
}
.enfa-order-history-backdrop {
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
}
.enfa-order-history-panel {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  max-width: 500px;
  background-color: #fff;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
}
.enfa-order-history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #eee;
}
.enfa-order-history-close {
  width: 44px;
  height: 44px;
  min-width: 44px;
  min-height: 44px;
  border: none;
  background: #f1f5f9;
  border-radius: 50%;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, color 0.15s, box-shadow 0.15s;
}
.enfa-order-history-close:hover {
  background: #e2e8f0;
  color: #0f172a;
}
.enfa-order-history-close:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(255, 107, 53, 0.4);
  background: #e2e8f0;
}
.enfa-order-history-body {
  padding: 1rem;
  overflow-y: auto;
  max-height: 70vh;
}
.order-item {
  border: 1px solid #eee;
  border-radius: 4px;
  margin-bottom: 1rem;
}
.order-item-header, .order-item-body, .order-item-footer {
  padding: 0.75rem;
}
.order-item-header {
  display: flex;
  justify-content: space-between;
  background-color: #f8f8f8;
}
.product-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0.35rem 0;
  gap: 0.5rem;
}
.product-info {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}
.item-modifiers {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-top: 0.15rem;
}
.modifier-tag {
  font-size: 0.75rem;
  color: #64748b;
  background-color: #f1f5f9;
  padding: 0.1rem 0.4rem;
  border-radius: 4px;
}
.order-item-footer {
    display: flex;
    justify-content: space-between;
    font-weight: bold;
}
.loading-state,
.empty-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 2.5rem 1rem;
  text-align: center;
  color: #64748b;
}
.error-state {
  color: #dc2626;
}
.loading-state p,
.empty-state p,
.error-state p {
  margin: 0;
  font-size: 0.95rem;
}
.btn-retry {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  padding: 0.5rem 1.25rem;
  background-color: #ff6b35;
  color: #ffffff;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background-color 0.15s ease, transform 0.1s ease;
}
.btn-retry:hover {
  background-color: #e55a2b;
}
.btn-retry:active {
  transform: scale(0.98);
}
.btn-retry:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(255, 107, 53, 0.4);
}
</style>
